/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  ModalBody, ModalFooter, Button, VStack, HStack, Text, 
  FormControl, FormLabel, Box,
  useToast,
  Textarea, FormHelperText,
  Select, Alert, AlertIcon, Grid, Icon
} from "@chakra-ui/react";
import { 
  FiSmartphone, FiClock, FiSave
} from "react-icons/fi";
import api from "utils/api";
import { getCurrentUser } from "services/authService";

const ImeiDegistirModal = ({ isOpen, onClose, aracData, onSuccess, onOpenImeiGecmisi }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    yeniImei: "",
    eskiImei: "",
    degisiklikNedeni: "",
    notlar: ""
  });
  const [imeiOptions, setImeiOptions] = useState([]);
  const [imeiLoading, setImeiLoading] = useState(false);

  // Kullanıcı rolünü yükle
  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setUserRole(user.role);
      })
      .catch(() => {
        setUserRole(null);
      });
  }, []);

  useEffect(() => {
    if (isOpen && aracData) {
      setFormData(prev => ({
        ...prev,
        eskiImei: aracData.imei || ""
      }));
      setImeiOptions([]);
      setImeiLoading(true);
      
      // Bayi ID belirleme: Eğer araç bir bayiye aitse, o bayinin stoklarından seç
      // Backend role-based filtreleme yapıyor:
      // - Bayi kullanıcısı: Backend otomatik olarak sadece kendi stoklarını filtreler
      // - Admin/Teknik eleman: Tüm stokları görebilir, ama araç bayiye aitse o bayinin stoklarını getirmek mantıklı
      const params = new URLSearchParams();
      params.append("durum", "hazir");
      
      // Araç bir bayiye aitse, o bayinin stoklarını getir
      // Admin ve teknik eleman için: araç bayiye aitse o bayinin stokları, değilse tüm stoklar
      // Bayi için: Backend zaten otomatik filtreler, ama araç kendi bayisine aitse o bayinin stoklarını görmesi için
      // bayi_id parametresini göndermemize gerek yok (backend zaten filtreler)
      // Ancak kod tutarlılığı için gönderebiliriz, backend zaten role kontrolü yapıyor
      if (aracData.bayi_id) {
        // Admin ve teknik eleman için: Araç bayiye aitse o bayinin stoklarını getir
        // Bayi için: Backend zaten otomatik filtreler, ama parametre göndermek zarar vermez
        params.append("bayi_id", aracData.bayi_id);
      }
      // Eğer araç bayiye ait değilse (bayi_id null/undefined) ve admin/teknik_eleman ise, 
      // tüm stokları getir (parametre göndermiyoruz, backend tüm stokları döner)
      
      api
        .get(`/api/stok-cihazlari/?${params.toString()}`)
        .then((res) => res.json())
        .then((data) => {
          const options = (Array.isArray(data) ? data : [])
            .filter((item) => item.durum === "hazir" && !item.in_use)
            .map((item) => ({
              value: item.imei,
              label: item.model ? `${item.imei} • ${item.model}` : item.imei,
            }));
          setImeiOptions(options);
        })
        .catch(() => setImeiOptions([]))
        .finally(() => setImeiLoading(false));
    }
  }, [isOpen, aracData, userRole]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImeiDegistir = async () => {
    setLoading(true);
    
    try {
      const response = await api.put(`/api/araclar/imei-degistir`, {
        aracId: aracData.id,
        eskiImei: formData.eskiImei,
        yeniImei: formData.yeniImei,
        degisiklikNedeni: formData.degisiklikNedeni,
        notlar: formData.notlar,
        kullanici: "admin", // Gerçek kullanıcı bilgisi
        zaman: new Date().toISOString()
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "IMEI Başarıyla Değiştirildi",
          description: `${formData.eskiImei} → ${formData.yeniImei}`,
          status: "success",
          duration: 5000,
          isClosable: true
        });
        
        onSuccess?.(result);
        onClose();
      } else {
        throw new Error(result.message || "IMEI değiştirilemedi");
      }

    } catch (error) {
      toast({
        title: "Hata",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const canSave = formData.yeniImei && formData.degisiklikNedeni;

  const renderForm = () => (
    <VStack spacing={5} align="stretch">
      {/* Mevcut IMEI Bilgisi */}
      <Box p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
        <HStack spacing={3} mb={2}>
          <Icon as={FiSmartphone} color="blue.500" />
          <Text fontWeight="semibold" color="blue.700">Mevcut IMEI Bilgisi</Text>
        </HStack>
        <Text fontSize="lg" fontFamily="mono" color="blue.800">{formData.eskiImei}</Text>
        <Text fontSize="sm" color="blue.600">Araç: {aracData?.plaka} | Firma: {aracData?.firma}</Text>
      </Box>

      {/* Yeni IMEI Girişi */}
      <FormControl isRequired>
        <FormLabel fontSize="sm" color="gray.600">Yeni IMEI</FormLabel>
        <Select
          variant="flushed"
          size="sm"
          placeholder={imeiLoading ? "Yükleniyor..." : "Hazır stoktan IMEI seçin"}
          value={formData.yeniImei}
          onChange={(e) => handleInputChange("yeniImei", e.target.value)}
          fontFamily="mono"
          isDisabled={imeiLoading || !imeiOptions.length}
        >
          {imeiOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <FormHelperText fontSize="xs" color="gray.500">
          Yalnızca stokta hazır ve kullanılmamış IMEI numaraları listelenir.
        </FormHelperText>
        {!imeiLoading && !imeiOptions.length && (
          <Text fontSize="xs" color="orange.500" mt={2}>
            Hazır durumda uygun IMEI bulunamadı.
          </Text>
        )}
      </FormControl>

      {/* Değişiklik Nedeni */}
      <FormControl isRequired>
        <FormLabel fontSize="sm" color="gray.600">Değişiklik Nedeni</FormLabel>
        <Select
          variant="flushed"
          size="sm"
          placeholder="Değişiklik sebebini seçin"
          value={formData.degisiklikNedeni}
          onChange={(e) => handleInputChange('degisiklikNedeni', e.target.value)}
        >
          <option value="Arıza">Arıza</option>
          <option value="Değişim">Değişim</option>
          <option value="Upgrade">Upgrade</option>
          <option value="Bakım">Bakım</option>
          <option value="Diğer">Diğer</option>
        </Select>
        <FormHelperText fontSize="xs" color="gray.500">
          Arıza seçilirse eski cihaz stokta arıza olarak işaretlenecektir.
        </FormHelperText>
      </FormControl>

      {/* Notlar */}
      <FormControl>
        <FormLabel fontSize="sm" color="gray.600">Ek Notlar (Opsiyonel)</FormLabel>
        <Textarea
          variant="flushed"
          size="sm"
          placeholder="Değişiklik ile ilgili ek bilgiler..."
          value={formData.notlar}
          onChange={(e) => handleInputChange('notlar', e.target.value)}
          rows={3}
        />
      </FormControl>

      {/* Özet Bilgiler */}
      {formData.yeniImei && formData.degisiklikNedeni && (
        <Box p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
          <Text fontWeight="semibold" mb={2} fontSize="sm">Değişiklik Özeti</Text>
          <Grid templateColumns="100px 1fr" gap={2} fontSize="xs">
            <Text color="gray.600">Araç:</Text>
            <Text>{aracData?.plaka}</Text>
            
            <Text color="gray.600">Eski IMEI:</Text>
            <Text fontFamily="mono">{formData.eskiImei}</Text>
            
            <Text color="gray.600">Yeni IMEI:</Text>
            <Text fontFamily="mono" color="blue.600">{formData.yeniImei}</Text>
            
            <Text color="gray.600">Neden:</Text>
            <Text>{formData.degisiklikNedeni}</Text>
          </Grid>
        </Box>
      )}

      {/* Uyarı */}
      {canSave && (
        <Alert status="info" size="sm">
          <AlertIcon />
          <VStack align="start" spacing={0}>
            <Text fontSize="xs" fontWeight="semibold">Önemli Uyarı!</Text>
            <Text fontSize="xs">
              Bu işlem sonrasında cihaz yeni IMEI ile TracCar sunucusuna bağlanmaya başlayacaktır. 
              Eski IMEI ile gelen veriler artık bu araçla eşleştirilmeyecektir.
            </Text>
          </VStack>
        </Alert>
      )}
    </VStack>
  );


  if (!aracData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered motionPreset="slideInBottom" closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              IMEI Değişikliği
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData.plaka} • {aracData.firma}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="600px" overflowY="auto">
          {renderForm()}
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <HStack spacing={3} w="full" justify="space-between">
            <Button
              variant="outline"
              leftIcon={<FiClock />}
              onClick={() => onOpenImeiGecmisi?.(aracData)}
              size="sm"
            >
              IMEI Geçmişi
            </Button>
            
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose} size="sm">
                İptal
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<FiSave />}
                onClick={handleImeiDegistir}
                isLoading={loading}
                loadingText="Değiştiriliyor..."
                isDisabled={!canSave}
                size="sm"
              >
                IMEI'yi Değiştir
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImeiDegistirModal;
